(function () {

  'use strict';


  /*
  ================================================================
  Z1H MYNTRA COLLECTION
  ================================================================
  */

  function initMyntraCollection(root) {

    if (!root || root.dataset.initialized === 'true') {
      return;
    }

    root.dataset.initialized = 'true';


    /*
    ================================================================
    ELEMENTS
    ================================================================
    */

    const cards = Array.from(
      root.querySelectorAll('[data-product-card]')
    );

    const grid = root.querySelector('[data-product-grid]');
    const count = root.querySelector('.z1h-results-count');
    const noResults = root.querySelector('[data-no-results]');
    const pagination = root.querySelector('[data-pagination]');
    const collectionButtons = root.querySelectorAll('[data-collection-filter]');
    const sortButtons = root.querySelectorAll('[data-sort]');

    const productsPerPage = parseInt(
      root.dataset.productsPerPage || '16',
      10
    );


    /*
    ================================================================
    STATE
    ================================================================
    */

    let activeCollection = 'all';
    let activeSort = 'recommended';
    let currentPage = 1;
    let visibleCards = [];

    let activeProduct = null;
    let selectedVariant = null;

    /*
    Handle of the product whose modal is currently loading / open.
    Prevents an older request from overwriting a newer one.
    */
    let loadingHandle = null;


    /*
    ================================================================
    PRODUCT DATA CACHE
    ================================================================

    Product JSON is fetched only once per product.

    Promise caching prevents duplicate requests if the user
    clicks the same product while it is still loading.
    ================================================================
    */

    const productCache = new Map();
    const productImageAltCache = new Map();


    /*
    ================================================================
    IMAGE URL / ALT HELPERS
    ================================================================
    */

    /*
    Normalises an image URL so the same image can be matched
    regardless of query string, protocol, or Shopify size suffix
    (e.g. image_600x.jpg -> image.jpg).
    */
    function normalizeImageUrl(url) {

      if (!url) {
        return '';
      }

      let result;

      try {

        const parsed = new URL(url, window.location.origin);

        result = parsed.origin + parsed.pathname;

      } catch (error) {

        result = String(url).split('?')[0].split('#')[0];

      }

      result = result.replace(
        /_(?:\d+x\d*|x\d+)(?=\.[a-zA-Z0-9]+$)/,
        ''
      );

      return result.toLowerCase();

    }


    function cleanAlt(value) {

      return String(value || '').trim().toLowerCase();

    }


    function getImageAltFromMap(imageAltMap, src) {

      if (!imageAltMap || !src) {
        return '';
      }

      return imageAltMap[normalizeImageUrl(src)] || '';

    }


    /*
    Fallback only: used when /products/{handle}.js has no media[]
    with alt text. Downloads and parses the product page HTML.
    */
    async function fetchProductImageAlts(handle) {

      if (!handle) {
        return {};
      }

      if (productImageAltCache.has(handle)) {
        return productImageAltCache.get(handle);
      }

      const request = fetch(
        window.Shopify.routes.root +
        'products/' +
        encodeURIComponent(handle),
        {
          method: 'GET',
          headers: { 'Accept': 'text/html' }
        }
      )
        .then(async function (response) {

          if (!response.ok) {
            return {};
          }

          const html = await response.text();

          const doc = new DOMParser().parseFromString(
            html,
            'text/html'
          );

          const imageAltMap = {};

          doc.querySelectorAll('img').forEach(function (img) {

            const alt = String(
              img.getAttribute('alt') || ''
            ).trim();

            if (!alt) {
              return;
            }

            const sources = [];

            const src = img.getAttribute('src');
            if (src) sources.push(src);

            const dataSrc = img.getAttribute('data-src');
            if (dataSrc) sources.push(dataSrc);

            const dataOriginal = img.getAttribute('data-original');
            if (dataOriginal) sources.push(dataOriginal);

            const srcset = img.getAttribute('srcset');

            if (srcset) {

              srcset.split(',').forEach(function (entry) {

                const value = entry.trim().split(/\s+/)[0];

                if (value) {
                  sources.push(value);
                }

              });

            }

            sources.forEach(function (source) {

              const normalized = normalizeImageUrl(source);

              if (normalized && !imageAltMap[normalized]) {
                imageAltMap[normalized] = alt;
              }

            });

          });

          return imageAltMap;

        })
        .catch(function (error) {

          console.warn(
            'Z1H: Unable to load image ALT data.',
            error
          );

          return {};

        });

      productImageAltCache.set(handle, request);

      return request;

    }


    /*
    ================================================================
    LOADER
    ================================================================
    */

    const loader = root.querySelector('[data-z1h-loader]');

    function hideLoader() {

      if (!loader) {
        return;
      }

      loader.style.opacity = '0';

      setTimeout(function () {
        loader.style.display = 'none';
      }, 250);

    }


    /*
    ================================================================
    RATING
    ================================================================
    */

    function getRating(card) {

      return parseFloat(card.dataset.rating || '0');

    }


    /*
    ================================================================
    SORT
    ================================================================
    */

    function sortCards(cardList) {

      return cardList.sort(function (a, b) {

        const priceA = parseFloat(a.dataset.price || '0');
        const priceB = parseFloat(b.dataset.price || '0');

        const createdA = parseInt(a.dataset.created || '0', 10);
        const createdB = parseInt(b.dataset.created || '0', 10);

        const salesA = parseFloat(a.dataset.bestSelling || '0');
        const salesB = parseFloat(b.dataset.bestSelling || '0');

        const ratingA = getRating(a);
        const ratingB = getRating(b);

        switch (activeSort) {

          case 'price-low':
            return priceA - priceB;

          case 'price-high':
            return priceB - priceA;

          case 'newest':
            return createdB - createdA;

          case 'best-selling':
            return salesB - salesA;

          case 'rating':
            return ratingB - ratingA;

          case 'recommended':
          default:
            return 0;

        }

      });

    }


    /*
    ================================================================
    COLLECTION FILTER
    ================================================================
    */

    function filterCards() {

      if (activeCollection === 'all') {
        return cards.slice();
      }

      return cards.filter(function (card) {

        const collections = (card.dataset.collections || '')
          .split(',')
          .filter(Boolean);

        return collections.includes(activeCollection);

      });

    }


    /*
    ================================================================
    SCROLL TO GRID
    ================================================================
    */

    function scrollToGrid() {

      if (!grid) {
        return;
      }

      const top =
        grid.getBoundingClientRect().top +
        window.pageYOffset -
        100;

      window.scrollTo({
        top: top,
        behavior: 'smooth'
      });

    }


    /*
    ================================================================
    PAGINATION
    ================================================================
    */

    function renderPagination(totalPages) {

      if (!pagination) {
        return;
      }

      pagination.innerHTML = '';

      if (totalPages <= 1) {
        return;
      }

      const fragment = document.createDocumentFragment();


      /* PREVIOUS */

      const previousButton = document.createElement('button');

      previousButton.type = 'button';
      previousButton.textContent = '‹';
      previousButton.disabled = currentPage === 1;

      previousButton.addEventListener('click', function () {

        if (currentPage > 1) {

          currentPage--;

          render();

          scrollToGrid();

        }

      });

      fragment.appendChild(previousButton);


      /* PAGE BUTTONS */

      for (let page = 1; page <= totalPages; page++) {

        const button = document.createElement('button');

        button.type = 'button';
        button.textContent = page;

        if (page === currentPage) {
          button.classList.add('is-active');
        }

        button.addEventListener('click', function () {

          currentPage = page;

          render();

          scrollToGrid();

        });

        fragment.appendChild(button);

      }


      /* NEXT */

      const nextButton = document.createElement('button');

      nextButton.type = 'button';
      nextButton.textContent = '›';
      nextButton.disabled = currentPage === totalPages;

      nextButton.addEventListener('click', function () {

        if (currentPage < totalPages) {

          currentPage++;

          render();

          scrollToGrid();

        }

      });

      fragment.appendChild(nextButton);

      pagination.appendChild(fragment);

    }


    /*
    ================================================================
    RENDER COLLECTION
    ================================================================
    */

    function render() {

      visibleCards = filterCards();

      visibleCards = sortCards(visibleCards);

      const totalProducts = visibleCards.length;

      const totalPages = Math.ceil(
        totalProducts / productsPerPage
      );

      if (currentPage > totalPages && totalPages > 0) {
        currentPage = totalPages;
      }

      const start = (currentPage - 1) * productsPerPage;
      const end = start + productsPerPage;

      const pageCards = visibleCards.slice(start, end);


      /* HIDE ALL CARDS */

      cards.forEach(function (card) {
        card.style.display = 'none';
      });


      /* SHOW CURRENT PAGE (DocumentFragment avoids repeated reflow) */

      if (grid) {

        const fragment = document.createDocumentFragment();

        pageCards.forEach(function (card) {

          card.style.display = '';

          fragment.appendChild(card);

        });

        grid.appendChild(fragment);

      }


      /* COUNT */

      if (count) {
        count.textContent = totalProducts;
      }


      /* EMPTY STATE */

      if (noResults) {
        noResults.hidden = totalProducts !== 0;
      }

      renderPagination(totalPages);

    }


    /*
    ================================================================
    COLLECTION BUTTONS
    ================================================================
    */

    collectionButtons.forEach(function (button) {

      button.addEventListener('click', function () {

        collectionButtons.forEach(function (item) {
          item.classList.remove('is-active');
        });

        button.classList.add('is-active');

        activeCollection = button.dataset.collectionFilter;

        currentPage = 1;

        render();

      });

    });


    /*
    ================================================================
    SORT BUTTONS
    ================================================================
    */

    sortButtons.forEach(function (button) {

      button.addEventListener('click', function () {

        sortButtons.forEach(function (item) {
          item.classList.remove('is-active');
        });

        button.classList.add('is-active');

        activeSort = button.dataset.sort;

        currentPage = 1;

        render();

      });

    });


    /*
    ================================================================
    COLLECTION CAROUSEL
    ================================================================
    */

    const carousel = root.querySelector('.z1h-collection-carousel');
    const carouselPrev = root.querySelector('.z1h-carousel-prev');
    const carouselNext = root.querySelector('.z1h-carousel-next');

    if (carouselPrev && carousel) {

      carouselPrev.addEventListener('click', function () {

        carousel.scrollBy({
          left: -250,
          behavior: 'smooth'
        });

      });

    }

    if (carouselNext && carousel) {

      carouselNext.addEventListener('click', function () {

        carousel.scrollBy({
          left: 250,
          behavior: 'smooth'
        });

      });

    }


    /*
    ================================================================
    PRODUCT MODAL
    ================================================================
    */

    const modal = root.querySelector('[data-quick-add-modal]');

    if (!modal) {

      hideLoader();

      render();

      return;

    }


    /*
    ================================================================
    MODAL ELEMENTS
    ================================================================
    */

    const modalTitle = modal.querySelector('[data-modal-title]');
    const modalRating = modal.querySelector('[data-modal-rating]');
    const modalPrice = modal.querySelector('[data-modal-price]');
    const modalSelection = modal.querySelector('[data-modal-selection]');
    const modalMainImage = modal.querySelector('[data-modal-main-image]');
    const modalThumbnails = modal.querySelector('[data-modal-thumbnails]');
    const modalSizes = modal.querySelector('[data-modal-sizes]');
    const modalAdd = modal.querySelector('[data-modal-add]');
    const modalProductLink = modal.querySelector('[data-modal-product-link]');


    /*
    ================================================================
    SIZE CHART
    ================================================================
    */

    const sizeChartButton = modal.querySelector('[data-size-chart-open]');
    const sizeChartModal = root.querySelector('[data-size-chart-modal]');
    const sizeChartTitle = root.querySelector('[data-size-chart-title]');
    const sizeChartContent = root.querySelector('[data-size-chart-content]');
    const sizeChartLoading = root.querySelector('[data-size-chart-loading]');
    const sizeChartError = root.querySelector('[data-size-chart-error]');


    /*
    ================================================================
    MONEY
    ================================================================
    */

    function formatMoney(cents) {

      const amount = Number(cents || 0) / 100;

      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-IN',
        {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 0
        }
      ).format(amount);

    }


    /*
    ================================================================
    ESCAPE HTML
    ================================================================
    */

    function escapeHtml(value) {

      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    }


    /*
    ================================================================
    GET LIGHTWEIGHT CARD DATA
    ================================================================
    */

    function getCardData(card) {

      const dataElement = card.querySelector(
        '[data-z1h-product-data]'
      );

      if (!dataElement) {
        return null;
      }

      try {

        return JSON.parse(dataElement.textContent);

      } catch (error) {

        console.error(
          'Z1H: Unable to parse product card data.',
          error
        );

        return null;

      }

    }


    /*
    ================================================================
    FETCH PRODUCT DATA

    Shopify's product JSON is requested only when the modal is
    opened. The request is cached.
    ================================================================
    */

    async function fetchProductData(handle) {

      if (!handle) {
        throw new Error('Product handle missing.');
      }

      if (productCache.has(handle)) {
        return productCache.get(handle);
      }

      const request = fetch(
        window.Shopify.routes.root +
        'products/' +
        encodeURIComponent(handle) +
        '.js',
        {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        }
      ).then(function (response) {

        if (!response.ok) {
          throw new Error('Unable to load product.');
        }

        return response.json();

      });

      /*
      Store the Promise, not only the result.
      This prevents duplicate simultaneous requests.
      */
      productCache.set(handle, request);

      try {

        return await request;

      } catch (error) {

        productCache.delete(handle);

        throw error;

      }

    }


    /*
    ================================================================
    RESOLVE FEATURED IMAGE ALT

    Finds the original alt text of the card's featured image by
    looking it up in the product media. Falls back to whatever alt
    the card's Liquid printed.
    ================================================================
    */

    function resolveFeaturedAlt(cardData, media) {

      const featuredSrc =
        cardData.featuredImage && cardData.featuredImage.src;

      const key = normalizeImageUrl(featuredSrc);

      if (key) {

        const match = media.find(function (item) {
          return normalizeImageUrl(item.src) === key;
        });

        if (match && match.alt) {
          return match.alt;
        }

      }

      return String(
        (cardData.featuredImage && cardData.featuredImage.alt) || ''
      ).trim();

    }


    /*
    ================================================================
    MERGE CARD DATA + SHOPIFY PRODUCT DATA
    ================================================================
    */

    function buildActiveProduct(cardData, productData, imageAltMap) {

      if (!productData) {
        return null;
      }

      const styleIndex = Number.isInteger(cardData.styleIndex)
        ? cardData.styleIndex
        : -1;

      const colourIndex = Number.isInteger(cardData.colourIndex)
        ? cardData.colourIndex
        : -1;

      const targetStyle = String(cardData.style || 'Default')
        .toLowerCase()
        .trim();

      const targetColour = String(cardData.colour || 'Default')
        .toLowerCase()
        .trim();


      /*
      --------------------------------------------------------------
      FILTER ONLY THE VARIANTS BELONGING TO THIS STYLE + COLOUR
      --------------------------------------------------------------
      */

      const variants = Array.isArray(productData.variants)
        ? productData.variants
            .filter(function (variant) {

              const options = Array.isArray(variant.options)
                ? variant.options
                : [];

              const variantStyle = styleIndex >= 0
                ? String(options[styleIndex] || 'Default')
                    .toLowerCase()
                    .trim()
                : 'default';

              const variantColour = colourIndex >= 0
                ? String(options[colourIndex] || 'Default')
                    .toLowerCase()
                    .trim()
                : 'default';

              return (
                variantStyle === targetStyle &&
                variantColour === targetColour
              );

            })
            .map(function (variant) {

              return {
                id: variant.id,
                available: variant.available,
                price: variant.price,
                compareAtPrice: variant.compare_at_price,
                title: variant.title,
                options: variant.options
              };

            })
        : [];


      /*
      --------------------------------------------------------------
      PRODUCT MEDIA (with real alt text)

      1. Preferred: productData.media[] from the .js endpoint.
      2. Fallback: productData.images[] + alt map scraped from the
         product page.
      --------------------------------------------------------------
      */

      let media = [];

      if (
        Array.isArray(productData.media) &&
        productData.media.length
      ) {

        media = productData.media
          .filter(function (item) {
            return item.media_type === 'image';
          })
          .map(function (item) {

            return {
              src: item.src,
              alt: String(item.alt || '').trim(),
              type: 'image'
            };

          });

      } else if (Array.isArray(productData.images)) {

        media = productData.media
  
        .filter(function (item) {
    
          return item.media_type === 'image';
  
        })
  
        .map(function (item) {
          return {
            id: item.id || item.media_id || '',
            src: item.src,
            alt: String(item.alt || '').trim(),
            type: 'image'
          };
        });
      }


      return {

        productId: productData.id || cardData.productId,

        handle: productData.handle || cardData.handle,

        title: productData.title || cardData.title,

        url: cardData.url || ('/products/' + cardData.handle),

        style: cardData.style,
        colour: cardData.colour,

        sizeIndex: cardData.sizeIndex,
        styleIndex: cardData.styleIndex,
        colourIndex: cardData.colourIndex,

        defaultVariant: cardData.defaultVariant,

        featuredImage: cardData.featuredImage,

        featuredAlt: resolveFeaturedAlt(cardData, media),

        variants: variants,

        media: media,

        sizeChart: cardData.sizeChart || {
          title: 'Size Chart',
          content: ''
        }

      };

    }


    /*
    ================================================================
    SHOW MODAL SHELL IMMEDIATELY
    ================================================================
    */

    function showModalShell(cardData) {

      if (modalTitle) {
        modalTitle.textContent = cardData.title || '';
      }

      if (modalSelection) {

        modalSelection.textContent = [
          cardData.style !== 'Default' ? cardData.style : '',
          cardData.colour !== 'Default' ? cardData.colour : ''
        ]
          .filter(Boolean)
          .join(' · ');

      }

      if (modalProductLink) {
        modalProductLink.href = cardData.url || '#';
      }


      /* Clear old content. */

      if (modalMainImage) modalMainImage.innerHTML = '';
      if (modalThumbnails) modalThumbnails.innerHTML = '';
      if (modalSizes) modalSizes.innerHTML = '';
      if (modalPrice) modalPrice.innerHTML = '';
      if (modalRating) modalRating.innerHTML = '';

      if (modalAdd) {
        modalAdd.disabled = true;
        modalAdd.textContent = 'Loading...';
      }


      /* Show the existing card image immediately. */

      if (
        modalMainImage &&
        cardData.featuredImage &&
        cardData.featuredImage.src
      ) {

        modalMainImage.innerHTML =
          '<img src="' +
          escapeHtml(cardData.featuredImage.src) +
          '" alt="' +
          escapeHtml(
            cardData.featuredImage.alt || cardData.title
          ) +
          '">';

      }


      /* Open immediately. */

      modal.classList.add('is-open');

      modal.setAttribute('aria-hidden', 'false');

      document.body.classList.add('z1h-modal-open');

    }


    /*
    ================================================================
    OPEN PRODUCT MODAL

    Flow:
    1. Open modal + show the card's featured image instantly.
    2. Fetch product data (cached).
    3. Read the featured image's original alt.
    4. Build the gallery from images with the same alt only.
    ================================================================
    */

    async function openModal(card) {

      const cardData = getCardData(card);

      if (!cardData) {

        console.error('Z1H: Product data not found.');

        return;

      }


      /* OPEN UI FIRST */

      showModalShell(cardData);

      loadingHandle = cardData.handle;


      /* LOADING STATE */

      if (modalSizes) {

        modalSizes.innerHTML =
          '<div class="z1h-modal-loading">Loading options...</div>';

      }


      try {

        /* FETCH ONLY NOW */

        const productData = await fetchProductData(cardData.handle);

        /*
        Modal was closed, or another product was opened
        while this request was loading.
        */
        if (
          !modal.classList.contains('is-open') ||
          loadingHandle !== cardData.handle
        ) {
          return;
        }


        /*
        Only scrape the product page when the .js response
        has no media[] with alt text.
        */

        let imageAltMap = null;

        const hasMediaAlts =
          Array.isArray(productData.media) &&
          productData.media.length > 0;

        if (!hasMediaAlts) {

          imageAltMap = await fetchProductImageAlts(cardData.handle);

          if (
            !modal.classList.contains('is-open') ||
            loadingHandle !== cardData.handle
          ) {
            return;
          }

        }


        activeProduct = buildActiveProduct(
          cardData,
          productData,
          imageAltMap
        );

        if (!activeProduct) {
          throw new Error('Unable to build product data.');
        }

        selectedVariant = null;


        /* RENDER DATA */

        renderModalRating(card);

        renderModalPrice();

        renderVariants();

        prepareSizeChartButton();


        /* PRODUCT URL */

        if (modalProductLink && activeProduct.url) {

          const firstVariant =
            activeProduct.variants && activeProduct.variants.length
              ? activeProduct.variants[0]
              : null;

          modalProductLink.href = firstVariant
            ? activeProduct.url + '?variant=' + firstVariant.id
            : activeProduct.url;

        }


        /*
        GALLERY

        Main image = featured image.
        Thumbnails are built in the next frame.
        */

        renderMainImageImmediately();

        requestAnimationFrame(function () {

          if (
            !activeProduct ||
            loadingHandle !== cardData.handle
          ) {
            return;
          }

          renderGalleryThumbnails();

        });

      } catch (error) {

        console.error('Z1H: Unable to load product.', error);

        if (loadingHandle !== cardData.handle) {
          return;
        }

        if (modalSizes) {

          modalSizes.innerHTML =
            '<div class="z1h-modal-error">Unable to load product options.</div>';

        }

        if (modalAdd) {
          modalAdd.disabled = true;
          modalAdd.textContent = 'Try Again';
        }

      }

    }


    /*
    ================================================================
    CLOSE PRODUCT MODAL
    ================================================================
    */

    function closeModal() {

      closeSizeChart();

      modal.classList.remove('is-open');

      modal.setAttribute('aria-hidden', 'true');

      document.body.classList.remove('z1h-modal-open');

      activeProduct = null;

      selectedVariant = null;

      loadingHandle = null;

    }


    /*
    ================================================================
    MODAL CLOSE BUTTONS
    ================================================================
    */

    modal
      .querySelectorAll('[data-modal-close]')
      .forEach(function (element) {

        element.addEventListener('click', closeModal);

      });


    /*
    ================================================================
    RATING
    ================================================================
    */

    function renderModalRating(card) {

      if (!modalRating) {
        return;
      }

      const rating = card.dataset.rating || '0';
      const ratingCount = card.dataset.ratingCount || '0';

      if (parseFloat(rating) > 0) {

        modalRating.innerHTML =
          '★ ' +
          parseFloat(rating).toFixed(1) +
          ' (' +
          ratingCount +
          ')';

      } else {

        modalRating.innerHTML = '';

      }

    }


    /*
    ================================================================
    BUILD MATCHED GALLERY

    1. The featured image is always first.
    2. Add only images whose alt equals the featured image's alt.
    3. Nothing else is shown.
    ================================================================
    */

function getGalleryImages() {

  if (!activeProduct) {
    return [];
  }

  const images = [];
  const usedKeys = new Set();

  /*
   * Key = file name only, without size suffix.
   * Works even when host / path differ between
   * Liquid image_url and the product .js media.
   */
  function fileKey(src) {

    if (!src) {
      return '';
    }

    let path = String(src).split('#')[0].split('?')[0];

    let name = path.substring(path.lastIndexOf('/') + 1);

    try {
      name = decodeURIComponent(name);
    } catch (error) {}

    return name
      .replace(/_(?:\d+x\d*|x\d+)(?:_crop_[a-z]+)?(?=\.[a-zA-Z0-9]+$)/i, '')
      .toLowerCase();

  }

  function addImage(src, alt) {

    const key = fileKey(src);

    if (!key || usedKeys.has(key)) {
      return;
    }

    usedKeys.add(key);

    images.push({
      src: src,
      alt: alt || activeProduct.title
    });

  }


  /* 1. FEATURED IMAGE (always first) */

  const featured = activeProduct.featuredImage;

  if (featured && featured.src) {
    addImage(
      featured.src,
      activeProduct.featuredAlt || activeProduct.title
    );
  }


  /* 2. SAME-ALT MEDIA */

  const targetAlt = cleanAlt(activeProduct.featuredAlt);

  if (targetAlt && Array.isArray(activeProduct.media)) {

    activeProduct.media.forEach(function (item) {

      if (!item || item.type !== 'image' || !item.src) {
        return;
      }

      if (cleanAlt(item.alt) !== targetAlt) {
        return;
      }

      addImage(item.src, item.alt);

    });

  }

  return images;

}


    /*
    ================================================================
    MAIN IMAGE

    Always starts with the featured image.
    ================================================================
    */

    function renderMainImageImmediately() {

      if (
        !modalMainImage ||
        !activeProduct ||
        !activeProduct.featuredImage ||
        !activeProduct.featuredImage.src
      ) {
        return;
      }

      showMainImage(activeProduct.featuredImage);

    }


    /*
    ================================================================
    RENDER GALLERY THUMBNAILS
    ================================================================
    */

    function renderGalleryThumbnails() {

      if (!activeProduct || !modalThumbnails) {
        return;
      }

      modalThumbnails.innerHTML = '';

      const images = getGalleryImages();

      if (!images.length) {
        return;
      }

      const fragment = document.createDocumentFragment();

      images.forEach(function (image, index) {

        const thumbnail = document.createElement('button');

        thumbnail.type = 'button';

        thumbnail.className = 'z1h-modal-thumbnail';

        if (index === 0) {
          thumbnail.classList.add('is-active');
        }

        const img = document.createElement('img');

        img.src = image.src;

        img.alt = image.alt || activeProduct.title;

        img.loading = index === 0 ? 'eager' : 'lazy';

        thumbnail.appendChild(img);

        thumbnail.addEventListener('click', function () {

          modalThumbnails
            .querySelectorAll('.z1h-modal-thumbnail')
            .forEach(function (item) {
              item.classList.remove('is-active');
            });

          thumbnail.classList.add('is-active');

          showMainImage(image);

        });

        fragment.appendChild(thumbnail);

      });

      modalThumbnails.appendChild(fragment);

    }


    /*
    ================================================================
    SHOW MAIN IMAGE
    ================================================================
    */

    function showMainImage(image) {

  if (!modalMainImage || !image || !image.src) {
    return;
  }

  const newSrc = normalizeImageUrl(image.src);

  /*
   * If this image is already being displayed,
   * do nothing.
   *
   * This prevents the featured image from being
   * removed and loaded again after product JSON
   * finishes loading.
   */
  const existingImage =
    modalMainImage.querySelector('img');

  if (
    existingImage &&
    normalizeImageUrl(existingImage.src) === newSrc
  ) {

    existingImage.alt =
      image.alt ||
      (activeProduct ? activeProduct.title : '');

    return;
  }

  /*
   * Only replace the image when the requested
   * image is actually different.
   */
  modalMainImage.innerHTML = '';

  const img = document.createElement('img');

  img.src = image.src;

  img.alt =
    image.alt ||
    (activeProduct ? activeProduct.title : '');

  img.decoding = 'async';
  img.loading = 'eager';

  modalMainImage.appendChild(img);

}


    /*
    ================================================================
    VARIANTS

    Selecting a size no longer swaps the main image. The gallery
    is defined entirely by the featured image's alt text.
    ================================================================
    */

    function renderVariants() {

      if (!modalSizes) {
        return;
      }

      modalSizes.innerHTML = '';

      selectedVariant = null;

      if (modalAdd) {
        modalAdd.disabled = true;
        modalAdd.textContent = 'Select Size';
      }

      if (
        !activeProduct ||
        !Array.isArray(activeProduct.variants) ||
        !activeProduct.variants.length
      ) {
        return;
      }

      const sizeIndex = Number.isInteger(activeProduct.sizeIndex)
        ? activeProduct.sizeIndex
        : -1;

      const fragment = document.createDocumentFragment();

      activeProduct.variants.forEach(function (variant) {

        let sizeName = variant.title || 'Select';

        if (
          sizeIndex >= 0 &&
          variant.options &&
          variant.options[sizeIndex]
        ) {
          sizeName = variant.options[sizeIndex];
        }

        const button = document.createElement('button');

        button.type = 'button';

        button.className = 'z1h-modal-size';

        button.textContent = sizeName;

        if (!variant.available) {

          button.disabled = true;

          button.classList.add('is-disabled');

        }

        button.addEventListener('click', function () {

          modalSizes
            .querySelectorAll('.z1h-modal-size')
            .forEach(function (item) {
              item.classList.remove('is-selected');
            });

          button.classList.add('is-selected');

          selectedVariant = variant;

          if (modalAdd) {
            modalAdd.disabled = false;
            modalAdd.textContent = 'Add to Bag';
          }

          if (modalProductLink && activeProduct.url) {

            modalProductLink.href =
              activeProduct.url + '?variant=' + variant.id;

          }

          renderModalPrice();

        });

        fragment.appendChild(button);

      });

      modalSizes.appendChild(fragment);


      /* ONE VARIANT / NO SIZE */

      if (
        sizeIndex < 0 &&
        activeProduct.variants.length === 1
      ) {

        const variant = activeProduct.variants[0];

        if (variant.available) {

          selectedVariant = variant;

          const button = modalSizes.querySelector('button');

          if (button) {
            button.classList.add('is-selected');
          }

          if (modalAdd) {
            modalAdd.disabled = false;
            modalAdd.textContent = 'Add to Bag';
          }

        }

      }

    }


    /*
    ================================================================
    PRICE
    ================================================================
    */

    function renderModalPrice() {

      if (!modalPrice || !activeProduct) {
        return;
      }

      const variants = Array.isArray(activeProduct.variants)
        ? activeProduct.variants
        : [];

      const variant =
        selectedVariant ||
        variants.find(function (item) {
          return item.available;
        }) ||
        variants[0];

      if (!variant) {
        return;
      }

      let html = '<span>' + formatMoney(variant.price) + '</span>';

      if (
        variant.compareAtPrice &&
        Number(variant.compareAtPrice) > Number(variant.price)
      ) {

        html +=
          ' <del>' +
          formatMoney(variant.compareAtPrice) +
          '</del>';

      }

      modalPrice.innerHTML = html;

    }


    /*
    ================================================================
    SIZE CHART BUTTON
    ================================================================
    */

    function prepareSizeChartButton() {

      if (!sizeChartButton) {
        return;
      }

      const hasSizeChart = !!(
        activeProduct &&
        activeProduct.sizeChart &&
        activeProduct.sizeChart.content
      );

      sizeChartButton.hidden = !hasSizeChart;

    }


    /*
    ================================================================
    LOAD SIZE CHART
    ================================================================
    */

    async function loadSizeChart() {

      if (
        !activeProduct ||
        !activeProduct.sizeChart ||
        !activeProduct.sizeChart.content ||
        !sizeChartModal
      ) {
        return;
      }

      const sizeChart = activeProduct.sizeChart;

      if (sizeChartLoading) {
        sizeChartLoading.hidden = false;
      }

      if (sizeChartError) {
        sizeChartError.hidden = true;
      }

      if (sizeChartContent) {
        sizeChartContent.innerHTML = '';
      }

      if (sizeChartTitle) {
        sizeChartTitle.textContent =
          sizeChart.title || 'Size Chart';
      }

      /* Open immediately. */

      sizeChartModal.classList.add('is-open');

      sizeChartModal.setAttribute('aria-hidden', 'false');

      /* Render content. */

      try {

        if (sizeChartContent) {
          sizeChartContent.innerHTML = sizeChart.content;
        }

      } catch (error) {

        console.error('Z1H Size Chart:', error);

        if (sizeChartError) {
          sizeChartError.hidden = false;
        }

      } finally {

        if (sizeChartLoading) {
          sizeChartLoading.hidden = true;
        }

      }

    }


    /*
    ================================================================
    SIZE CHART OPEN
    ================================================================
    */

    if (sizeChartButton) {

      sizeChartButton.addEventListener('click', function (event) {

        event.preventDefault();

        event.stopPropagation();

        loadSizeChart();

      });

    }


    /*
    ================================================================
    CLOSE SIZE CHART
    ================================================================
    */

    function closeSizeChart() {

      if (!sizeChartModal) {
        return;
      }

      sizeChartModal.classList.remove('is-open');

      sizeChartModal.setAttribute('aria-hidden', 'true');

    }

    if (sizeChartModal) {

      sizeChartModal
        .querySelectorAll('[data-size-chart-close]')
        .forEach(function (button) {

          button.addEventListener('click', closeSizeChart);

        });

    }


    /*
    ================================================================
    QUICK ADD EVENT DELEGATION

    ONE listener for the whole collection instead of one listener
    for every product button.
    ================================================================
    */

    root.addEventListener('click', function (event) {

      const button = event.target.closest('[data-quick-add]');

      if (!button || !root.contains(button)) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      const card = button.closest('[data-product-card]');

      if (!card) {
        return;
      }

      openModal(card);

    });


    /*
    ================================================================
    ADD TO CART
    ================================================================
    */

    if (modalAdd) {

      modalAdd.addEventListener('click', async function () {

        if (!selectedVariant) {
          return;
        }

        const originalText = 'Add to Bag';

        modalAdd.disabled = true;

        modalAdd.textContent = 'Adding...';

        try {

          const response = await fetch(
            window.Shopify.routes.root + 'cart/add.js',
            {
              method: 'POST',

              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },

              body: JSON.stringify({
                items: [
                  {
                    id: Number(selectedVariant.id),
                    quantity: 1
                  }
                ]
              })
            }
          );

          if (!response.ok) {
            throw new Error('Unable to add product');
          }

          modalAdd.textContent = 'Added ✓';

          document.dispatchEvent(new CustomEvent('cart:refresh'));

          document.dispatchEvent(new CustomEvent('cart:updated'));

          document.dispatchEvent(
            new CustomEvent('z1h:cart-updated', {
              detail: {
                product: activeProduct,
                variant: selectedVariant
              }
            })
          );

          setTimeout(function () {

            modalAdd.textContent = originalText;

            modalAdd.disabled = false;

            closeModal();

          }, 700);

        } catch (error) {

          console.error(error);

          modalAdd.disabled = false;

          modalAdd.textContent = 'Try Again';

        }

      });

    }


    /*
    ================================================================
    ESCAPE KEY
    ================================================================
    */

    document.addEventListener('keydown', function (event) {

      if (event.key !== 'Escape') {
        return;
      }

      if (
        sizeChartModal &&
        sizeChartModal.classList.contains('is-open')
      ) {

        closeSizeChart();

        return;

      }

      if (modal.classList.contains('is-open')) {

        closeModal();

      }

    });


    /*
    ================================================================
    INITIAL RENDER
    ================================================================
    */

    render();

    /*
    Hide loader after initial collection rendering
    has had an opportunity to paint.
    */

    requestAnimationFrame(function () {

      setTimeout(hideLoader, 150);

    });

  }


  /*
  ================================================================
  INITIALIZE
  ================================================================
  */

  function initialize() {

    document
      .querySelectorAll('.z1h-myntra')
      .forEach(initMyntraCollection);

  }

  if (document.readyState === 'loading') {

    document.addEventListener('DOMContentLoaded', initialize);

  } else {

    initialize();

  }


  /*
  ================================================================
  SHOPIFY THEME EDITOR
  ================================================================
  */

  document.addEventListener('shopify:section:load', function (event) {

    const section = event.target;

    const root = section.querySelector('.z1h-myntra');

    if (root) {
      initMyntraCollection(root);
    }

  });

})();